/*
  Automatic cart gifts
  --------------------
  Keeps promotional gift lines in step with what is actually in the cart. Each
  rule says: when a trigger line is present, a gift line must exist; when the
  trigger goes away, the gift goes with it.

  Gifts are always added as ONE-TIME lines (no selling plan). For subscription
  promos that is what keeps renewals correct: Skio builds the contract from
  selling-plan lines only, so a one-time gift cannot recur.

  Only lines carrying a rule's own property are ever touched. A gift product
  that arrived some other way - a discount app, or the customer adding it
  themselves - is left alone.

  Bundle gifts (rule.bundle) follow the bundle line the bundles landing page
  adds: one of each gift its size gives, gone with the bundle.

  Cart attributes in config.cartAttributes (the A/B offer test arm) are kept
  on the cart through the same queue, so they are there before checkout
  whichever path added the items.

  Public API: window.CartGifts.reconcile() -> Promise<boolean changed>
*/
(function () {
  var config = window.CART_GIFTS_CONFIG;
  if (!config) return;

  var rules = config.rules || [];
  var cartAttributes = config.cartAttributes || {};
  if (rules.length === 0 && Object.keys(cartAttributes).length === 0) return;

  // Marks our own cart writes so the fetch hook below does not react to them.
  var MARKER = 'X-Cart-Gifts';
  var RELOAD_GUARD_KEY = 'cart-gifts-reloaded';

  // Every cart write goes through this chain. Shopify's cart endpoints are not
  // safe to call in parallel: concurrent writes read stale state and undo each
  // other, which is exactly how duplicate gift lines appear.
  var queue = Promise.resolve();

  function log(message, detail) {
    if (config.debug) console.log('[cart-gifts] ' + message, detail === undefined ? '' : detail);
  }

  function isManaged(line) {
    if (!line.properties) return false;
    return rules.some(function (rule) {
      return !!line.properties[rule.property];
    });
  }

  function post(url, payload) {
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    headers[MARKER] = '1';

    return fetch(url, { method: 'POST', headers: headers, body: JSON.stringify(payload) });
  }

  function getCart() {
    var headers = { Accept: 'application/json' };
    headers[MARKER] = '1';

    return fetch('/cart.js', { headers: headers }).then(function (response) {
      return response.json();
    });
  }

  function slotKey(property, variantId) {
    return property + '|' + variantId;
  }

  // One code, several separated by commas, or an array of them. Several exist
  // because the same gift is promoted through more than one channel - an
  // abandoned-cart flow wants its own code so its performance can be read
  // apart from the rest.
  function parseCodes(value) {
    var list = Array.isArray(value) ? value : String(value == null ? '' : value).split(',');

    return list.map(function (entry) {
      return String(entry == null ? '' : entry).trim().toLowerCase();
    }).filter(Boolean);
  }

  // Any of those codes, applied to the session via /discount/CODE. A code typed
  // at checkout never reaches the cart, so it cannot be seen here.
  // Deliberately ignores the "applicable" flag: a discount that gives an item
  // away is not applicable until that item is in the cart, which is exactly
  // what this rule is about to fix.
  function appliedDiscountCode(cart, codes) {
    var wanted = parseCodes(codes);
    if (wanted.length === 0 || !cart.discount_codes) return null;

    var match = null;
    cart.discount_codes.some(function (entry) {
      var applied = String(entry && entry.code || '').trim();
      if (wanted.indexOf(applied.toLowerCase()) === -1) return false;
      match = applied;
      return true;
    });

    return match;
  }

  function hasDiscountCode(cart, codes) {
    return appliedDiscountCode(cart, codes) !== null;
  }

  // Attributes the cart is missing or holds a different value for. Only once
  // there is something in the cart: an empty cart is never checked out, and
  // writing to it would create a cart for every visitor who only browses.
  function attributeChanges(cart) {
    if (!cart.items || cart.items.length === 0) return null;

    var current = cart.attributes || {};
    var changes = null;

    Object.keys(cartAttributes).forEach(function (name) {
      var want = cartAttributes[name];
      if (want === null || want === undefined || String(current[name]) === String(want)) return;

      changes = changes || {};
      changes[name] = want;
    });

    return changes;
  }

  // A rule with dates (the Halloween toy) gives nothing outside them, and its
  // gifts already in a cart go. Checked here rather than in Liquid alone: a
  // cached page can outlive the dates it was rendered with. A date that does
  // not read closes the rule - better no gift than one outside its dates.
  function inDates(rule, now) {
    var from = rule.activeFrom ? Date.parse(rule.activeFrom) : -Infinity;
    var until = rule.activeUntil ? Date.parse(rule.activeUntil) : Infinity;
    if (isNaN(from) || isNaN(until)) return false;
    return now >= from && now <= until;
  }

  // The bundle in the cart, for the bundle gift rules: the first one-time line
  // carrying the bundle property, of a bundle product, at exactly one of the
  // bundle quantities - the same line the bundle-discount function prices. A
  // quantity changed by hand is no bundle, so its gifts go.
  function bundleLine(cart, bundle) {
    var found = null;
    cart.items.some(function (line) {
      if (!line.properties || !line.properties[bundle.property]) return false;
      if (line.selling_plan_allocation) return false;
      if (bundle.productIds.indexOf(line.product_id) === -1) return false;
      if (bundle.quantities.indexOf(line.quantity) === -1) return false;
      found = line;
      return true;
    });
    return found;
  }

  // Lines that can trigger a rule, before any quantity condition.
  function triggers(rule, line) {
    // A gift line must never trigger a gift, or rules could feed each other.
    if (isManaged(line)) return false;
    if (rule.requireSellingPlan && !line.selling_plan_allocation) return false;
    if (rule.triggerProductIds && rule.triggerProductIds.indexOf(line.product_id) === -1) return false;
    return true;
  }

  // What the cart should look like, and what it looks like now.
  function plan(cart) {
    var existing = {};
    var duplicates = [];

    // Index by distinct property, not by rule: several rules may share one
    // property, and indexing per rule would count a single line as a duplicate
    // of itself and delete it.
    var properties = rules.map(function (rule) {
      return rule.property;
    }).filter(function (property, index, all) {
      return all.indexOf(property) === index;
    });

    cart.items.forEach(function (line) {
      properties.forEach(function (property) {
        if (!line.properties || !line.properties[property]) return;

        var key = slotKey(property, line.variant_id);
        if (existing[key]) duplicates.push(line.key);
        else existing[key] = { key: line.key, quantity: line.quantity };
      });
    });

    var desired = {};
    // Extra properties for a line about to be added, by slot: which code
    // brought it (rule.codeProperty), so checkout can tell a free bag that came
    // for COMEBACK from the BOGO one. Never written to a line already there.
    var marks = {};
    // The most any onePerOrder rule allows per property, so two such rules
    // sharing a property - BOGO and COMEBACK on _free_bogo_gift - still add up
    // to one free bag between them.
    var orderCaps = {};

    var now = Date.now();

    rules.forEach(function (rule) {
      var maxQty = Math.max(0, parseInt(rule.maxQty, 10) || 0);
      if (!inDates(rule, now)) maxQty = 0;
      if (maxQty === 0) return;

      // The code sits on the cart rather than on any line, so it gates the
      // whole rule before a single item is looked at.
      var code = rule.requireDiscountCode ? appliedDiscountCode(cart, rule.requireDiscountCode) : null;
      if (rule.requireDiscountCode && code === null) return;

      if (rule.onePerOrder) orderCaps[rule.property] = Math.max(orderCaps[rule.property] || 0, maxQty);

      // A bundle gift belongs to the bundle as a whole, not to any line's
      // quantity: one of it when the cart's bundle is a size that gives it.
      if (rule.bundle) {
        var anchor = bundleLine(cart, rule.bundle);
        if (!anchor || !rule.giftVariantId || rule.bundle.giftFor.indexOf(anchor.quantity) === -1) return;

        var bundleKey = slotKey(rule.property, rule.giftVariantId);
        desired[bundleKey] = Math.max(desired[bundleKey] || 0, maxQty);
        return;
      }

      // A code rule with no line conditions is satisfied by the code alone:
      // there is no trigger line to find, and the cart may even be empty.
      if (rule.requireDiscountCode && !rule.triggerProductIds && !rule.requireSellingPlan) {
        if (!rule.giftVariantId) return;

        var codeKey = slotKey(rule.property, rule.giftVariantId);
        desired[codeKey] = Math.max(desired[codeKey] || 0, maxQty);
        return;
      }

      // minQty counts every trigger unit in the cart together, the way the
      // discount function and Skio's volume rules do.
      var minQty = Math.max(0, parseInt(rule.minQty, 10) || 0);
      var triggerQty = 0;
      if (minQty > 0) {
        cart.items.forEach(function (line) {
          if (triggers(rule, line)) triggerQty += line.quantity;
        });
      }

      // onePerOrder caps the rule at maxQty across the whole cart rather than
      // per trigger: "a free bag", not one per product.
      var budget = rule.onePerOrder ? maxQty : Infinity;

      cart.items.forEach(function (line) {
        if (!triggers(rule, line)) return;
        if (minQty > 0 && triggerQty < minQty) return;
        if (budget <= 0) return;

        // No giftVariantId means the gift is another unit of the trigger itself.
        var variantId = rule.giftVariantId || line.variant_id;
        var key = slotKey(rule.property, variantId);
        var qty = Math.min(minQty > 0 ? triggerQty : line.quantity, maxQty, budget);

        if (rule.onePerOrder) budget -= qty;
        desired[key] = Math.max(desired[key] || 0, qty);

        if (rule.codeProperty && code) {
          marks[key] = marks[key] || {};
          marks[key][rule.codeProperty] = code.toUpperCase();
        }
      });
    });

    // Each onePerOrder rule already keeps to its own cap. Rules sharing a
    // property agree on the slot - same triggers, same cart order - so this
    // only bites if they ever disagree, and then keeps the first.
    Object.keys(orderCaps).forEach(function (property) {
      var left = orderCaps[property];
      Object.keys(desired).forEach(function (key) {
        if (key.split('|')[0] !== property) return;
        desired[key] = Math.min(desired[key], left);
        left -= desired[key];
      });
    });

    // A rule with yieldToGiven steps aside when its gift product is in the
    // cart for free already: another rule wants it (the TOYFREE toy is the
    // bundle's toy), or a $0.00 line of it came from elsewhere (an app). The
    // customer gets one of each gift product, never two. A paid line of the
    // same product - one the customer bought - does not count.
    rules.forEach(function (rule) {
      if (!rule.yieldToGiven || !rule.giftVariantId) return;

      var ownKey = slotKey(rule.property, rule.giftVariantId);
      if (!desired[ownKey]) return;

      var variant = String(rule.giftVariantId);
      var wantedElsewhere = Object.keys(desired).some(function (key) {
        var parts = key.split('|');
        return parts[0] !== rule.property && parts[1] === variant && desired[key] > 0;
      });
      var freeElsewhere = cart.items.some(function (line) {
        if (String(line.variant_id) !== variant || isManaged(line)) return false;
        var price = typeof line.final_price === 'number' ? line.final_price : line.price;
        return price === 0;
      });

      if (wantedElsewhere || freeElsewhere) desired[ownKey] = 0;
    });

    // A gift must never be the whole order. Applying the code to an empty cart,
    // or emptying it afterwards, otherwise leaves the customer checking out
    // with nothing but the free item - verified on the live store: the toy on
    // its own with TOYFREE comes to $0.00.
    //
    // "Paid" rather than merely "not ours" on purpose: a cart holding our gift
    // next to some other free line still adds up to nothing.
    //
    // A subscription line counts as paid at its plan price even when this
    // order discounts it to zero: the customer commits to the renewals. COMEBACK
    // typed or linked does exactly that for a moment - the code makes one
    // subscribed bag free until the free bag line exists - and judged by its
    // final price the cart would hold nothing paid, so the free bag that ends
    // the moment would never be added.
    var hasPaidLine = cart.items.some(function (line) {
      if (isManaged(line)) return false;
      if (line.selling_plan_allocation && line.price > 0) return true;
      var price = typeof line.final_price === 'number' ? line.final_price : line.price;
      return price > 0;
    });

    if (!hasPaidLine) {
      Object.keys(desired).forEach(function (key) {
        desired[key] = 0;
      });
    }

    var operations = [];

    duplicates.forEach(function (key) {
      operations.push({ type: 'change', key: key, quantity: 0 });
    });

    Object.keys(existing).forEach(function (key) {
      var want = desired[key] || 0;
      if (existing[key].quantity !== want) {
        operations.push({ type: 'change', key: existing[key].key, quantity: want });
      }
    });

    Object.keys(desired).forEach(function (key) {
      if (existing[key] || desired[key] <= 0) return;

      var parts = key.split('|');
      var operation = {
        type: 'add',
        property: parts[0],
        id: parseInt(parts[1], 10),
        quantity: desired[key]
      };
      if (marks[key]) operation.marks = marks[key];
      operations.push(operation);
    });

    var attributes = attributeChanges(cart);
    if (attributes) operations.push({ type: 'attributes', attributes: attributes });

    return operations;
  }

  function apply(operation) {
    if (operation.type === 'attributes') {
      return post('/cart/update.js', { attributes: operation.attributes });
    }

    if (operation.type === 'change') {
      return post('/cart/change.js', { id: operation.key, quantity: operation.quantity });
    }

    var properties = {};
    Object.keys(operation.marks || {}).forEach(function (name) {
      properties[name] = operation.marks[name];
    });
    properties[operation.property] = 'true';

    // A sold-out gift must not block the purchase, so failures are swallowed:
    // the customer still checks out, just without the free item.
    return post('/cart/add.js', {
      id: operation.id,
      quantity: operation.quantity,
      properties: properties
    }).then(function (response) {
      if (!response.ok) log('gift could not be added, continuing without it', operation.id);
    });
  }

  function run(operations) {
    return operations.reduce(function (chain, operation) {
      return chain.then(function () {
        return apply(operation).catch(function (error) {
          log('operation failed', error);
        });
      });
    }, Promise.resolve());
  }

  // Identifies a set of cart writes, so the reload guard below can tell "the
  // same thing all over again" from "something new happened".
  function signature(operations) {
    return operations.map(function (operation) {
      if (operation.type === 'attributes') return 'attributes:' + JSON.stringify(operation.attributes);
      return operation.type + ':' + (operation.key || operation.id) + ':' + operation.quantity;
    }).sort().join('|');
  }

  // The lines as the page was rendered with them, in the same form as
  // config.renderedCart, which Liquid writes on the cart page.
  function cartSignature(cart) {
    return (cart.items || []).map(function (line) {
      return line.key + ':' + line.quantity + '|';
    }).join('');
  }

  // Resolves to the signature of the writes performed, or null if none were.
  function reconcile() {
    return getCart().then(function (cart) {
      var operations = plan(cart);
      log('operations', operations);

      if (operations.length === 0) {
        // Nothing to write, but the cart page may still have been rendered
        // before writes made elsewhere landed: an add to cart on the PDP
        // reconciles there while the browser is already on its way to /cart,
        // and the page then shows a cart without its free bag. If the cart
        // differs from what was rendered, ask for the same reload a write would.
        if (onCartPage() && typeof config.renderedCart === 'string') {
          var current = cartSignature(cart);
          if (current !== config.renderedCart) return 'rendered:' + current;
        }
        return null;
      }

      var stamp = signature(operations);
      return run(operations).then(function () {
        return stamp;
      });
    }).catch(function (error) {
      log('reconcile failed', error);
      return null;
    });
  }

  /* ----- overlay ----------------------------------------------------------
     Reconciling means several round trips to Shopify and, on the cart page, a
     re-render afterwards. Without a cover the customer watches line items and
     totals shift under the cursor. Liquid renders the overlay already visible
     when it knows a reconcile is pending, so there is no flash either way. */

  function overlay() {
    return document.querySelector('[data-cart-gifts-overlay]');
  }

  function showOverlay() {
    var element = overlay();
    if (element) element.classList.remove('is-hidden');
  }

  function hideOverlay() {
    var element = overlay();
    if (element) element.classList.add('is-hidden');
  }

  function onCartPage() {
    return window.location.pathname.replace(/\/$/, '').endsWith('/cart');
  }

  function lastReloadAttempt() {
    try { return sessionStorage.getItem(RELOAD_GUARD_KEY); } catch (error) { return null; }
  }

  function rememberReloadAttempt(stamp) {
    try { sessionStorage.setItem(RELOAD_GUARD_KEY, stamp); } catch (error) { /* private mode */ }
  }

  function forgetReloadAttempt() {
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch (error) { /* private mode */ }
  }

  function reconcileAndRefresh() {
    showOverlay();

    queue = queue.then(reconcile, reconcile).then(function (stamp) {
      if (!stamp) {
        forgetReloadAttempt();
        hideOverlay();
        return false;
      }

      if (!onCartPage()) {
        hideOverlay();
        return true;
      }

      // Loop protection, keyed on what was actually written rather than on a
      // flag saying "we have reloaded before". A sticky flag gets stranded the
      // moment a page navigates away before the settling reconcile runs, and
      // from then on every add is left showing a stale cart. Comparing
      // signatures means only a genuine repeat - the same writes again right
      // after a reload, so reloading cannot help - stops the reload.
      if (lastReloadAttempt() === stamp) {
        log('skipping reload, the previous one did not settle the cart');
        forgetReloadAttempt();
        hideOverlay();
        return true;
      }

      rememberReloadAttempt(stamp);
      // Overlay stays up through the reload so the stale cart is never shown.
      window.location.reload();
      return true;
    });

    return queue;
  }

  // For pages that add to the cart and then leave for checkout themselves -
  // the old flow pages post with jQuery, which the fetch hook below does not
  // see, and redirect at once. Reconciling first puts the free bag and the
  // _offer_arm attribute on the cart before checkout reads it. A reconcile
  // that hangs must never keep the customer from checkout, hence the timeout.
  var SETTLE_TIMEOUT = 4000;

  function goWhenSettled(url) {
    var gone = false;
    function go() {
      if (gone) return;
      gone = true;
      window.location.href = url;
    }

    setTimeout(go, SETTLE_TIMEOUT);
    reconcileAndRefresh().then(go, go);
  }

  window.CartGifts = { reconcile: reconcileAndRefresh, goWhenSettled: goWhenSettled };

  // React to cart writes made by the rest of the theme (product forms, quantity
  // controls, apps). Requests we made ourselves carry the marker header.
  var debounce = null;
  var nativeFetch = window.fetch;

  function isOurs(input, init) {
    var headers = (init && init.headers) || (input && input.headers);
    if (!headers) return false;
    if (typeof headers.get === 'function') return headers.get(MARKER) !== null;
    return Object.keys(headers).some(function (name) {
      return name.toLowerCase() === MARKER.toLowerCase();
    });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartWrite = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
    var ours = isOurs(input, init);

    return nativeFetch.apply(this, arguments).then(function (response) {
      if (isCartWrite && !ours && response.ok) {
        clearTimeout(debounce);
        debounce = setTimeout(reconcileAndRefresh, 200);
      }
      return response;
    });
  };

  // The cart as Liquid rendered it. A visitor whose cart predates their arm,
  // or was filled by a plain form post that bypassed fetch, would otherwise
  // reach checkout without the attribute.
  function attributesStale() {
    var onLoad = config.cartOnLoad;
    if (!onLoad || !onLoad.itemCount) return false;
    return attributeChanges({ items: [true], attributes: onLoad.attributes }) !== null;
  }

  // On the cart page, also check the render against the cart (see reconcile).
  var renderedCartToCheck = typeof config.renderedCart === 'string' && onCartPage();

  if (config.autoReconcile || attributesStale() || renderedCartToCheck) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', reconcileAndRefresh);
    } else {
      reconcileAndRefresh();
    }
  } else {
    hideOverlay();
  }
})();
